import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  superAdminLogin,
  superAdminLogout,
  adminRegister,
  editUserProfile,
  addNewUserAdmin,
  fetchUser,
  verifyIdentity,
  updateAdminDetails,
  getDashboardData,
  fetchAllCards,
  getSubAdminDashboardData,
  getFreeUserAdmin,
  forgetPassword,
  resetPassword,
  updateAdminCoachAgreementDetails
} from "../../api/authApi";

// Thunks for handling async actions
export const loginUser = createAsyncThunk(
  "adminauth/login",
  async (credentials, thunkAPI) => {
    try {
      const response = await superAdminLogin(credentials);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const registerUser = createAsyncThunk(
  "adminauth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await adminRegister(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const verifyIdentityUser = createAsyncThunk(
  "auth/verifyIdentity",
  async (sessionData, thunkAPI) => {
    try {
      const response = await verifyIdentity(sessionData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "adminauth/getCurrentUser",
  async (_, thunkAPI) => {
    try {
      const response = await fetchUser();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getDashboardDataAdmin = createAsyncThunk(
  "adminauth/getDashboardDataAdmin",
  async (date, thunkAPI) => {
    try {
      const response = await getDashboardData(date);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getSubAdminDashboardDetails = createAsyncThunk(
  "adminauth/getSubAdminDashboardDetails",
  async (date, thunkAPI) => {
    try {
      const response = await getSubAdminDashboardData();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addNewUser = createAsyncThunk(
  "adminauth/addNewUser",
  async (userData, thunkAPI) => {
    try {
      const response = await addNewUserAdmin(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const editUser = createAsyncThunk(
  "auth/editUser",
  async ({ formData, userId }, thunkAPI) => {
    try {
      const response = await editUserProfile(formData, userId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateAdminData = createAsyncThunk(
  "auth/updateAdminData",
  async (data, thunkAPI) => {
    try {
      const response = await updateAdminDetails(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateAdminCoachAgreement = createAsyncThunk(
  "auth/updateAdminCoachAgreement",
  async (data, thunkAPI) => {
    try {
      const response = await updateAdminCoachAgreementDetails(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "adminauth/logout",
  async (_, thunkAPI) => {
    try {
      const response = await superAdminLogout();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchUserCards = createAsyncThunk(
  "auth/getCards",
  async (_, thunkAPI) => {
    try {
      const response = await fetchAllCards();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const getFreeUserData = createAsyncThunk(
  "auth/getFreeUserAdminData",
  async (_, thunkAPI) => {
    try {
      const response = await getFreeUserAdmin();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const forgetPasswordUser = createAsyncThunk(
  "auth/forgetPassword",
  async (email, thunkAPI) => {
    try {
      const response = await forgetPassword(email);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const changePasswordUser = createAsyncThunk(
  "auth/resetPassword",
  async (userData, thunkAPI) => {
    try {
      const response = await resetPassword(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
// Initial state
const initialState = {
  admin:
    JSON.parse(
      ["undefined", "null", undefined, null].includes(
        localStorage.getItem("admin")
      )
        ? null
        : localStorage.getItem("admin")
    ) || null,
  isAuthenticated: !!localStorage.getItem("adminToken"),
  dashboardData: {},
  subAdminBillingDetails: {},
  cards: [],
  filterData: {},
  freeUserNeeded: null,
  loading: false,
  error: null
};

const adminauthSlice = createSlice({
  name: "adminauth",
  initialState,
  reducers: {
    setPendingAgreementChecked: (state, action) => {
      state.admin.referenceData.isPendingCheckAgreement =
        action.payload.isPendingCheckAgreement;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.admin = action.payload.user;
        if (action.payload.user?.isActive) {
          state.isAuthenticated = true;
        }
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(forgetPasswordUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(changePasswordUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(verifyIdentityUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyIdentityUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        if (action.payload.user?.isActive) {
          state.isAuthenticated = true;
        }
        state.loading = false;
      })
      .addCase(verifyIdentityUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        if (action.payload?.user?.userType === "Affiliate") {
          state.user = action.payload.user;
          state.isAuthenticated = true;
        }
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.admin = action.payload.user;
        if (action.payload.filterData)
          state.filterData = action.payload.filterData;
        if (action.payload.user?.isActive) {
          state.isAuthenticated = true;
        }
        state.loading = false;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.isAuthenticated = false;
        localStorage.removeItem("adminToken");
        localStorage.removeItem("admin");
      })
      .addCase(getDashboardDataAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDashboardDataAdmin.fulfilled, (state, action) => {
        state.dashboardData = action.payload;
        state.loading = false;
      })
      .addCase(getDashboardDataAdmin.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getSubAdminDashboardDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubAdminDashboardDetails.fulfilled, (state, action) => {
        state.subAdminBillingDetails = action.payload;
        state.loading = false;
      })
      .addCase(getSubAdminDashboardDetails.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserCards.fulfilled, (state, action) => {
        state.cards = action.payload.cards;
      })
      .addCase(getFreeUserData.fulfilled, (state, action) => {
        state.freeUserNeeded = action.payload.isSuperAdmin;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.admin = null;
        state.isAuthenticated = false;
        state.loading = false;
      });
  }
});
export const { setPendingAgreementChecked } = adminauthSlice.actions;

export default adminauthSlice.reducer;
